import * as React from 'react';
import { FixMeLater } from '../../utils';
import { ButtonBaseProps } from '../ButtonBase/ButtonBase';
import { ColorOption, OptionCornerStyle, useComponentDefaultProps } from '../../theming';
import { Styler, StylerProps } from '../Styler/Styler';
import { useStyles } from './Option.styles';
import { SvgIconProps } from '../SvgIcon/SvgIcon';

export type OptionTheme = {
    borderWidth: number;
    paddingX: number;
    paddingY: number;
    densePaddingY: number;
    spacingX: number;
    selectedColor: ColorOption;
};

export const optionTheme = {
    borderWidth: 2,
    paddingX: 2,
    paddingY: 18,
    densePaddingY: 10,
    spacingX: 16,
    selectedColor: 'secondary.base',
};

export interface OptionProps extends StylerProps, ButtonBaseProps {
    /**
     * If `true`, the option will be in the selected state.
     * @default false
     */
    selected?: boolean;

    /**
     * An svg icon component.
     */
    icon?: React.ReactElement<SvgIconProps>;

    /**
     * An svg icon component.
     */
    actionIcon?: React.ReactElement<SvgIconProps>;

    /**
     * The options's corner style. It might be preferable to set this on the theme using default props.
     * @default square
     */
    cornerStyle?: OptionCornerStyle;

    /**
     * If `true`, default left and right padding will be applied according to the theme gutter settings.
     * @default false
     */
    defaultGutters?: boolean;
}

const defaultProps: Partial<OptionProps> = {
    cornerStyle: 'square',
};

export const Option: React.ForwardRefExoticComponent<OptionProps & React.RefAttributes<HTMLElement>> = React.forwardRef(
    function Option(props: OptionProps, ref: React.ForwardedRef<FixMeLater>): React.JSX.Element {
        const {
            children,
            selected,
            icon,
            actionIcon,
            cornerStyle = 'square',
            defaultGutters = false,
            ...other
        } = useComponentDefaultProps('Option', defaultProps, props);

        const { styles } = useStyles({ cornerStyle });

        return (
            <Styler
                ref={ref}
                baseStyles={styles.root}
                data-option={true}
                data-selected={selected || undefined}
                data-default-gutters={defaultGutters || undefined}
                {...other}
            >
                {icon &&
                    React.cloneElement(icon, {
                        css: styles.icon,
                        'data-icon': 'true',
                    })}
                <div data-content={true}>{children}</div>
                {actionIcon &&
                    React.cloneElement(actionIcon, {
                        css: styles.actionIcon,
                        'data-action-icon': 'true',
                    })}
            </Styler>
        );
    }
);
